import { isGroupMissing, TeamsLinked } from '../utils/teamGroupRule';

/** The Add Ticket form's state, as far as its required-field rule reads it. */
export interface RequiredFieldsState {
  Entity?: Record<string, unknown>;
  Requester?: unknown;
  /** Required fields the agent has changed - from then on each says when it is empty. */
  RequiredTouched?: Record<string, boolean>;
}

/** A custom property, as far as the rule reads it. */
interface RequiredProperty {
  PropertyId: string;
  IsRequired?: boolean;
  IsArchived?: boolean;
  IsHidden?: boolean;
}

/** Status, Type, Category and Priority, by their TicketActions names. */
const REQUIRED_ENUMS = ['StatusId', 'TicketTypeId', 'CategoryId', 'PriorityId'];

/**
 * Status, Type, Category and Priority start on their defaults, so one is empty only when the agent has emptied it -
 * and only then does it hold Create back, as on Edit Ticket. A field never touched is left to the server, as before:
 * a tenant without ticket types, say, has no Type to fill.
 */
export const emptiedRequiredEnum = (data: RequiredFieldsState): boolean =>
  REQUIRED_ENUMS.some(field => data.RequiredTouched?.[field] && !data.Entity?.[field]);

/** A required custom property with no answer. An answer blob that cannot be read counts as no answers. */
const requiredPropertyEmpty = (answerJson: unknown, properties: RequiredProperty[]): boolean => {
  let answers: Record<string, unknown> = {};
  try {
    answers = JSON.parse(typeof answerJson === 'string' && answerJson.length > 0 ? answerJson : '{}');
  } catch {
    answers = {};
  }
  return (properties ?? []).some(p => !p.IsArchived && !p.IsHidden && p.IsRequired && !answers[p.PropertyId]);
};

/**
 * Whether a field Add Ticket requires is empty - Create stays off until none is (Shaun, 2026-10-20): Request,
 * Requester, Team, a Group when the team has groups, an emptied Status / Type / Category / Priority, and every
 * required custom property.
 */
export const addTicketMissingRequired = (
  data: RequiredFieldsState,
  teams: TeamsLinked[],
  supportGroups: TeamsLinked[],
  customProperties: RequiredProperty[]
): boolean =>
  !data.Entity?.Title || !data.Requester || !data.Entity?.TeamId || emptiedRequiredEnum(data)
  || isGroupMissing(data.Entity?.TeamId as number, data.Entity?.SupportGroupId as number, teams, supportGroups)
  || requiredPropertyEmpty(data.Entity?.CustomPropertiesAnswerJson, customProperties);
